import { useState, useEffect } from 'react';
import { X, Send, Search, User, Phone, Mail, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPicker({ onSend, onClose }) {
  const [mode, setMode] = useState('friends'); // friends | manual
  const [searchQuery, setSearchQuery] = useState('');
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  // Manual entry
  const [manualName, setManualName] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const [manualEmail, setManualEmail] = useState('');

  useEffect(() => {
    if (mode === 'friends') fetchFriends();
  }, [mode]);

  const fetchFriends = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/users/search`, {
        params: { q: searchQuery || 'a', limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriends(res.data || []);
    } catch {
      setFriends([]);
    }
    setLoading(false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const res = await axios.get(`${API}/users/search`, {
        params: { q: searchQuery, limit: 20 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setFriends(res.data || []);
    } catch {
      setFriends([]);
    }
    setLoading(false);
  };

  const selectFriend = (friend) => {
    setSelected({
      name: friend.name || friend.username,
      username: friend.username,
      user_id: friend.id,
      avatar: friend.avatar,
    });
  };

  const handleSendFriend = () => {
    if (!selected) return;
    onSend(selected);
  };

  const handleSendManual = () => {
    if (!manualName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (!manualPhone.trim() && !manualEmail.trim()) {
      toast.error('Please enter a phone or email');
      return;
    }
    onSend({
      name: manualName.trim(),
      phone: manualPhone.trim() || undefined,
      email: manualEmail.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50" data-testid="contact-picker">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <h3 className="font-bold text-slate-800">Share Contact</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 rounded-full" data-testid="contact-close-btn">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-stone-100">
          <button
            onClick={() => setMode('friends')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mode === 'friends' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500'}`}
            data-testid="contact-tab-friends"
          >
            <User className="w-4 h-4 inline mr-1.5" />DharmBook Users
          </button>
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${mode === 'manual' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-slate-500'}`}
            data-testid="contact-tab-manual"
          >
            <Plus className="w-4 h-4 inline mr-1.5" />Manual Entry
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {mode === 'friends' ? (
            <>
              {/* Search */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search DharmBook users..."
                  className="flex-1 px-4 py-2.5 bg-stone-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-300"
                  data-testid="contact-search-input"
                />
                <button
                  onClick={handleSearch}
                  disabled={loading}
                  className="px-4 py-2.5 bg-orange-500 text-white rounded-xl font-medium text-sm hover:bg-orange-600 disabled:opacity-50"
                  data-testid="contact-search-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </button>
              </div>

              {/* Results */}
              {loading ? (
                <div className="py-8 flex justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : friends.length === 0 ? (
                <p className="text-center text-slate-500 text-sm py-8">No users found</p>
              ) : (
                <div className="space-y-1">
                  {friends.map((friend) => (
                    <button
                      key={friend.id}
                      onClick={() => selectFriend(friend)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                        selected?.user_id === friend.id ? 'bg-orange-50 ring-2 ring-orange-300' : 'hover:bg-stone-50'
                      }`}
                      data-testid={`contact-friend-${friend.id}`}
                    >
                      <img
                        src={friend.avatar || `https://api.dicebear.com/7.x/initials/svg?seed=${friend.name || friend.username}`}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover border border-stone-200"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 text-sm truncate">{friend.name || friend.username}</p>
                        <p className="text-xs text-slate-500">@{friend.username}</p>
                      </div>
                      {selected?.user_id === friend.id && (
                        <div className="w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Manual Entry Form */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Name *</label>
                  <input
                    type="text"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    placeholder="Contact name"
                    className="w-full px-4 py-2.5 bg-stone-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-orange-300"
                    data-testid="manual-name-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Phone</label>
                  <div className="flex items-center gap-2 bg-stone-100 rounded-xl px-4">
                    <Phone className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input
                      type="tel"
                      value={manualPhone}
                      onChange={(e) => setManualPhone(e.target.value)}
                      placeholder="+91 98765 43210"
                      className="flex-1 py-2.5 bg-transparent text-sm outline-none"
                      data-testid="manual-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1 block">Email</label>
                  <div className="flex items-center gap-2 bg-stone-100 rounded-xl px-4">
                    <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <input
                      type="email"
                      value={manualEmail}
                      onChange={(e) => setManualEmail(e.target.value)}
                      placeholder="contact@example.com"
                      className="flex-1 py-2.5 bg-transparent text-sm outline-none"
                      data-testid="manual-email-input"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Send Button */}
        <div className="px-4 py-3 border-t border-stone-100">
          <button
            onClick={mode === 'friends' ? handleSendFriend : handleSendManual}
            disabled={mode === 'friends' ? !selected : !manualName.trim()}
            className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="send-contact-btn"
          >
            <Send className="w-4 h-4" /> Send Contact
          </button>
        </div>
      </div>
    </div>
  );
}
